import { Link } from '@mui/material'
import PropTypes from 'prop-types'
import useAuth from '@/hooks/useAuth'
import { canPeek } from './peekAccess'
import { useEntityPeek } from './peekContext'

/**
 * A reference to another record — a meter's location, a location's meter —
 * that opens that record in the peek panel instead of making the reader go and
 * find it. Readers who couldn't open that record (see peekAccess.js) get plain
 * text. Use it wherever a table or panel names a related record; register
 * new record types in `registry.jsx`.
 */
export default function EntityLink({ type, id, locationId, children, sx }) {
    const peek = useEntityPeek()
    const { user } = useAuth()
    if (!peek || id == null || !canPeek(user, type, { id, locationId })) {
        return <>{children}</>
    }
    return (
        <Link
            component="button"
            type="button"
            underline="hover"
            aria-haspopup="dialog"
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                peek.open(type, id)
            }}
            sx={[
                {
                    font: 'inherit',
                    textAlign: 'left',
                    verticalAlign: 'baseline'
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
            ]}
        >
            {children}
        </Link>
    )
}

EntityLink.propTypes = {
    children: PropTypes.node.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // A meter's Location id: lets a location member peek an in-scope meter.
    locationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    type: PropTypes.string.isRequired
}
